import { Mail, MessageSquare, Phone, Play, Pause, Settings, Sparkles, BarChart3, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Sequence, useUpdateSequence, useDeleteSequence } from "@/hooks/useOutreach";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const channelIcons: Record<string, typeof Mail> = {
  email: Mail,
  linkedin: MessageSquare,
  sms: MessageSquare,
  whatsapp: MessageSquare,
  call: Phone,
};

interface SequenceCardProps {
  sequence: Sequence & {
    enrollments_count?: number;
    active_count?: number;
    completed_count?: number;
    replied_count?: number;
  };
}

export function SequenceCard({ sequence }: SequenceCardProps) {
  const updateSequence = useUpdateSequence();
  const deleteSequence = useDeleteSequence();
  
  const isActive = sequence.status === "active";
  const isPaused = sequence.status === "paused";

  const handleToggleStatus = () => {
    const newStatus = isActive ? "paused" : "active";
    updateSequence.mutate({ id: sequence.id, status: newStatus });
  };

  const handleDelete = () => {
    deleteSequence.mutate(sequence.id);
  };

  // Calculate mock metrics based on enrollments (in production, these would come from real data)
  const enrolled = sequence.enrollments_count || 0;
  const replied = sequence.replied_count || 0;
  const completed = sequence.completed_count || 0;
  const openRate = enrolled > 0 ? Math.round((completed / enrolled) * 100) : 0;
  const replyRate = enrolled > 0 ? Math.round((replied / enrolled) * 100) : 0;

  return (
    <div className="glass rounded-xl p-5 hover:border-border transition-all duration-200">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-semibold text-foreground">{sequence.name}</h3>
          <div className="flex items-center gap-2 mt-1">
            {sequence.channels.map((channel) => {
              const Icon = channelIcons[channel] || MessageSquare;
              return (
                <div
                  key={channel}
                  className="p-1 rounded bg-muted"
                  title={channel}
                >
                  <Icon className="h-3 w-3 text-muted-foreground" />
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "stage-pill text-xs px-2 py-1 rounded-full",
              isActive
                ? "bg-success/10 text-success"
                : isPaused
                ? "bg-warning/10 text-warning"
                : "bg-muted text-muted-foreground"
            )}
          >
            {sequence.status.charAt(0).toUpperCase() + sequence.status.slice(1)}
          </span>
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <Settings className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Description */}
      {sequence.description && (
        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
          {sequence.description}
        </p>
      )}

      {/* Metrics */}
      <div className="grid grid-cols-4 gap-4 mb-4">
        <div>
          <p className="text-2xl font-bold">{enrolled}</p>
          <p className="text-xs text-muted-foreground">Enrolled</p>
        </div>
        <div>
          <p className="text-2xl font-bold">{openRate}%</p>
          <p className="text-xs text-muted-foreground">Open Rate</p>
        </div>
        <div>
          <p className="text-2xl font-bold">{replyRate}%</p>
          <p className="text-xs text-muted-foreground">Reply Rate</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-success">{replied}</p>
          <p className="text-xs text-muted-foreground">Replies</p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 pt-4 border-t border-border/50">
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "gap-2",
            isActive
              ? "text-warning hover:text-warning"
              : "text-success hover:text-success"
          )}
          onClick={handleToggleStatus}
          disabled={updateSequence.isPending}
        >
          {isActive ? (
            <>
              <Pause className="h-3.5 w-3.5" /> Pause
            </>
          ) : (
            <>
              <Play className="h-3.5 w-3.5" /> {isPaused ? "Resume" : "Activate"}
            </>
          )}
        </Button>
        <Button variant="outline" size="sm" className="gap-2">
          <BarChart3 className="h-3.5 w-3.5" /> Analytics
        </Button>
        <Button variant="ghost" size="sm" className="gap-2">
          <Sparkles className="h-3.5 w-3.5" /> AI Optimize
        </Button>
        
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2 ml-auto text-destructive hover:text-destructive">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Sequence</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete "{sequence.name}"? This action cannot be undone and will remove all associated enrollments.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
